// Display exposure: how the renderer maps the world's light, which spans about nine decades (noon 1,
// a full moon 1e-6, a moonless night 1e-9), onto a screen that shows about three.
//
// One gain, a pure function of the luminance of the eye's light (eyeLum.ts: the sky plus the sun's beam on a
// horizontal surface on the ground, and the light of a surface facing the star as the planet shrinks below). It scales tone-mapping exposure and the sky colour,
// and the stars and moon go through the same gain, so their visibility against the sky falls out of
// the physical flux ratios. There is no adaptation state and no time constant: the sun message is
// already eased, so the gain moves as smoothly as the light does.
//
//   gain = (L_REF / L) ^ EXP_HIGH            above the knee (partial compensation: night still reads
//                                            darker than day)
//   below the knee the exponent falls to EXP_LOW, so very dim light is compressed more and a moonlit
//   night does not come out as bright as a dusk.
// A_MAX caps the gain, which is what sets how dark a moonless night gets: the sky shows about
// 6 * A_MAX * (starlight + airglow), about 2e-3 linear (6/255 on screen) at 2e5 with a moonless floor
// of about 1.6e-9. Lower it for a darker night, raise it for a brighter one.
//
// The ground at night. The sky is drawn straight through 1 - exp(-6 * gain * E), which is linear in the
// light for a dark sky, but lit surfaces go through the renderer's tone mapper, and ACES has a toe that
// clips to black everything below about 0.4 percent: under a moon the sky is a visible blue-grey and the
// ground is pure black. installNightToneMapping() swaps in a tone mapper that is exactly ACES in the
// day and blends to a toe-free curve as the exposure rises past dusk, so a surface of albedo a under
// the sky shows about a times the sky's own brightness, which is what the light says.
//
// Night colour (scotopic shift). By eye, a moonlit scene is silvery blue-grey, because at that level the
// rods carry vision: they are almost blind to red, they cannot encode hue, and dim neutral stimuli read
// as cool. The light field itself is physically warm (a sunbeam off a slightly red moon, reddened more
// when low, falling on warm sand), so a straight render comes out brown. SCOTOPIC blends the display
// toward a rod-weighted luminance with a cool tint as the night curve takes over. It happens only in the
// display (this tone mapper and the sky colour), so the world server's light field and the organism's
// light sensors still see the physical spectrum.
import * as THREE from 'three';

export const EXPOSURE = {
  L_REF:    0.85,    // luminance at which the gain is 1
  EXP_HIGH: 0.75,
  EXP_LOW:  0.45,
  L_KNEE:   1e-4,
  A_MAX:    2e5,
};

/** Gain for a luminance of open-ground illumination (sun = 1 at the world). */
export function exposureGain(lum: number): number {
  const L = Math.max(lum, 1e-12);
  const g = L >= EXPOSURE.L_KNEE
    ? Math.pow(EXPOSURE.L_REF / L, EXPOSURE.EXP_HIGH)
    : Math.pow(EXPOSURE.L_REF / EXPOSURE.L_KNEE, EXPOSURE.EXP_HIGH) * Math.pow(EXPOSURE.L_KNEE / L, EXPOSURE.EXP_LOW);
  return Math.min(EXPOSURE.A_MAX, Math.max(1, g));
}


export const NIGHT_TONE = {
  FROM: 2000,   // tone-mapping exposure (base * gain) at which the blend to the night curve starts
  TO:   9000,   // ... and where it is complete (a full moon is about 1e4; dusk is a few hundred)
  K:    3.0,    // night curve scale: 6 (the sky curve) * pi / (ambient gain 4.8 * base exposure 1.3), so
                // a surface shows albedo * the sky's brightness
};

export const SCOTOPIC = {
  STRENGTH: 0.85,                    // 0 = off, 1 = fully rod-driven (monochrome blue-grey)
  ROD: [0.01, 0.40, 0.60] as const,  // rod weights on linear sRGB (red barely registers); normalised below
  TINT: [0.82, 0.96, 1.15] as const, // colour of the achromatic rod signal; scaled to keep luminance
};

const rodNorm = SCOTOPIC.ROD[0] + SCOTOPIC.ROD[1] + SCOTOPIC.ROD[2];
export const SCOTOPIC_ROD_W = SCOTOPIC.ROD.map(w => w / rodNorm) as [number, number, number];
const rodW = SCOTOPIC_ROD_W;
const tintLum = 0.2126 * SCOTOPIC.TINT[0] + 0.7152 * SCOTOPIC.TINT[1] + 0.0722 * SCOTOPIC.TINT[2];
export const SCOTOPIC_TINT_N = SCOTOPIC.TINT.map(t => t / tintLum) as [number, number, number];
const rodTint = SCOTOPIC_TINT_N;

/** 0 in the day, 1 at night: the same blend the tone mapper uses, from tone-mapping exposure. */
export function nightFactor(toneExposure: number): number {
  const t = Math.min(1, Math.max(0, (toneExposure - NIGHT_TONE.FROM) / (NIGHT_TONE.TO - NIGHT_TONE.FROM)));
  return t * t * (3 - 2 * t);
}

/**
 * Apply the scotopic shift to a linear colour (used for the sky and fog, which bypass the tone mapper).
 * Pass the tone-mapping exposure (base * gain). Returns a new [r, g, b]; unchanged in the day.
 */
export function scotopicShift(rgb: ArrayLike<number>, toneExposure: number): [number, number, number] {
  const k = SCOTOPIC.STRENGTH * nightFactor(toneExposure);
  if (k <= 0) return [rgb[0], rgb[1], rgb[2]];
  const rod = rodW[0] * rgb[0] + rodW[1] * rgb[1] + rodW[2] * rgb[2];
  return [
    rgb[0] + (rod * rodTint[0] - rgb[0]) * k,
    rgb[1] + (rod * rodTint[1] - rgb[1]) * k,
    rgb[2] + (rod * rodTint[2] - rgb[2]) * k,
  ];
}

/**
 * Replace three's CustomToneMapping stub with ACES blended into a toe-free curve by exposure, and report
 * whether that worked. Call before the first material compiles, and use THREE.CustomToneMapping only if
 * it returns true (otherwise keep ACESFilmicToneMapping, which is the same minus the night ground).
 */
export function installNightToneMapping(): boolean {
  const chunks = THREE.ShaderChunk as unknown as Record<string, string>;
  const src = chunks.tonemapping_pars_fragment;
  const stub = 'vec3 CustomToneMapping( vec3 color ) { return color; }';
  if (typeof src !== 'string') return false;
  if (src.includes('NIGHT_TONE')) return true;                     // already installed
  if (!src.includes(stub) || !src.includes('vec3 ACESFilmicToneMapping')) return false;
  chunks.tonemapping_pars_fragment = src.replace(stub, `
// NIGHT_TONE: ACES by day, a toe-free soft-clipped line by night, blended by exposure.
vec3 CustomToneMapping( vec3 color ) {
  vec3 aces = ACESFilmicToneMapping( color );
  float night = smoothstep( ${NIGHT_TONE.FROM.toFixed(1)}, ${NIGHT_TONE.TO.toFixed(1)}, toneMappingExposure );
  vec3 lin = color * ( toneMappingExposure * ${NIGHT_TONE.K.toFixed(3)} );
  float rod = dot( lin, vec3( ${rodW.map(w => w.toFixed(4)).join(', ')} ) );
  lin = mix( lin, rod * vec3( ${rodTint.map(t => t.toFixed(4)).join(', ')} ), ${SCOTOPIC.STRENGTH.toFixed(3)} * night );
  lin = lin / ( 1.0 + lin );
  return mix( aces, lin, night );
}`);
  return true;
}
